import classNames from "classnames";
import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { uniqBy } from "lodash";

import { Message, Form, FormMessage, Accordion, Button, HintView, Grid } from "lib/components/index";
import st from "lib/utils/index";
import * as FormControl from "lib/components/FormControl";
import { ProductVariantDecorator } from "lib/decorators/index";
import openProductVariantMigrateModal from "./ProductVariantMigrateModal";
import Hint from "./StepDescriptionHint";
import { stateSelector, updateProduct, validate, save } from "../index";

class ProductVariantFormSection extends React.PureComponent {
  handleTranslationPropChange(translation, prop, value) {
    const { product_variant: pv, onChange } = this.props;
    const nextTranslation = { ...translation, [prop]: value };
    onChange({
      ...pv,
      translations: pv.translations
        .concat(pv.translations.find((x) => x.uuid === nextTranslation.uuid) ? [] : [nextTranslation])
        .map((x) => (x.uuid === nextTranslation.uuid ? nextTranslation : x)),
    });
  }

  render() {
    const {
      readOnlyAccess,
      locale,
      product,
      product_variant,
      errors,
      name,
      onBlur,
      onRemove,
      onMigrateAttributesClick,
    } = this.props;
    const translation = product_variant.translations.find((t) => t.locale === locale) || {
      uuid: st.generateUuid(),
      locale,
    };
    const translationName = `${name}.translations[${translation.uuid}]`;

    return (
      <div>
        {!!errors[name] && <FormMessage error={errors[name]} />}

        <FormControl.InputFormGroup
          {...FormControl.addProps(`${translationName}.name`, { errors })}
          value={translation.name}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, translation, "name")}
          label={I18n.humanAttributeName("product", "name")}
          required
          autoFocus={product_variant._focus}
          readOnly={readOnlyAccess}
        />

        <FormControl.FormattableTextAreaFormGroup
          {...FormControl.addProps(`${translationName}.short_description`, {
            errors,
          })}
          value={translation.short_description}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, translation, "short_description")}
          label={I18n.humanAttributeName("product", "short_description")}
          autoHeight
          readOnly={readOnlyAccess}
        />

        <FormControl.MultipleInputFormGroup
          {...FormControl.addProps(`${translationName}.included_items`, {
            errors,
          })}
          value={translation.included_items}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, translation, "included_items")}
          label={I18n.humanAttributeName("product", "included_items")}
          placeholder={I18n.humanAttributeName("product", "included_items_placeholder")}
          readOnly={readOnlyAccess}
        />

        {translation.deprecated_v1_includes_content !== null &&
          translation.deprecated_v1_includes_content !== undefined && (
            <FormControl.FormattableTextAreaFormGroup
              {...FormControl.addProps(`${translationName}.deprecated_v1_includes_content`, { errors })}
              value={translation.deprecated_v1_includes_content}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "deprecated_v1_includes_content")}
              label={I18n.humanAttributeName("product", "deprecated_v1_includes_content")}
              readOnly={readOnlyAccess}
            />
          )}

        <FormControl.MultipleInputFormGroup
          {...FormControl.addProps(`${translationName}.excluded_items`, {
            errors,
          })}
          value={translation.excluded_items}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, translation, "excluded_items")}
          label={I18n.humanAttributeName("product", "excluded_items")}
          placeholder={I18n.humanAttributeName("product", "excluded_items_placeholder")}
          readOnly={readOnlyAccess}
        />

        {translation.deprecated_v1_excludes_content !== null &&
          translation.deprecated_v1_excludes_content !== undefined && (
            <FormControl.FormattableTextAreaFormGroup
              {...FormControl.addProps(`${translationName}.deprecated_v1_excludes_content`, { errors })}
              value={translation.deprecated_v1_excludes_content}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "deprecated_v1_excludes_content")}
              label={I18n.humanAttributeName("product", "deprecated_v1_excludes_content")}
              readOnly={readOnlyAccess}
            />
          )}

        <FormControl.MultipleInputFormGroup
          {...FormControl.addProps(`${translationName}.customer_requirements`, {
            errors,
          })}
          value={translation.customer_requirements}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, translation, "customer_requirements")}
          label={I18n.humanAttributeName("product", "customer_requirements")}
          placeholder={I18n.humanAttributeName("product", "customer_requirements_placeholder")}
          readOnly={readOnlyAccess}
        />

        {translation.deprecated_v1_requirements_content !== null &&
          translation.deprecated_v1_requirements_content !== undefined && (
            <FormControl.FormattableTextAreaFormGroup
              {...FormControl.addProps(`${translationName}.deprecated_v1_requirements_content`, { errors })}
              value={translation.deprecated_v1_requirements_content}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "deprecated_v1_requirements_content")}
              label={I18n.humanAttributeName("product", "deprecated_v1_requirements_content")}
              readOnly={readOnlyAccess}
            />
          )}

        {!readOnlyAccess && (
          <Button
            as="a"
            color="red"
            basic
            onClick={onRemove}
            content={I18n.t("papp.products.EditProductView.StepDescription.remove_variant")}
          />
        )}
        {product.product_type !== "coupon" && !readOnlyAccess && (
          <Button
            as="a"
            basic
            floated="right"
            onClick={onMigrateAttributesClick}
            content={I18n.t("papp.products.EditProductView.StepDescription.migrate_data_from_other_product")}
          />
        )}
      </div>
    );
  }
}

@connect(
  (state) => ({
    currentCompany: state.auth.company.model,
    product: stateSelector(state).product,
    submitLoading: stateSelector(state).submitLoading,
    valid: stateSelector(state).valid,
    errors: stateSelector(state).errors,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        updateProduct,
        validate,
        save,
      },
      dispatch
    )
)
export default class StepDescriptionForm extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      activeIndex: 0,
    };
  }

  handleOpenAccordion = (e, { index }) => {
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({ activeIndex: newIndex });
  };

  handleSubmit(event) {
    const { save } = this.props;
    event.preventDefault();
    save();
  }

  handlePropChange(prop, value) {
    const { product, updateProduct } = this.props;
    updateProduct({
      ...product,
      [prop]: value,
    });
  }

  handleTranslationPropChange(translation, prop, value) {
    const { product, updateProduct, isTranslating } = this.props;
    if (isTranslating) return;
    const nextTranslation = { ...translation, [prop]: value };
    const translations = product.translations
      .concat(product.translations.find((x) => x.uuid === nextTranslation.uuid) ? [] : [nextTranslation])
      .map((x) => (x.uuid === nextTranslation.uuid ? nextTranslation : x));
    updateProduct({
      ...product,
      translations,
    });
  }

  handlePvTranslationPropChange(pv, translation, prop, value) {
    const nextTranslation = { ...translation, [prop]: value };
    const translations = pv.translations
      .concat(pv.translations.find((x) => x.uuid === nextTranslation.uuid) ? [] : [nextTranslation])
      .map((x) => (x.uuid === nextTranslation.uuid ? nextTranslation : x));
    this.handleProductVariantChange(
      new ProductVariantDecorator({
        ...pv,
        _focus: undefined,
        translations,
      })
    );
  }

  handleProductVariantAdd() {
    const { product, updateProduct } = this.props;
    const defaultPv = product.product_variants[0];
    updateProduct({
      ...product,
      product_variants: product.product_variants
        .map((pv, pvi) => ({
          ...pv,
          translations: pv.translations.map((t) => ({
            ...t,
            name:
              t.name ||
              I18n.t("papp.products.EditProductView.StepDescription.new_variant_default_name", {
                index: pvi + 1,
                locale: t.locale,
                defaultValue: I18n.t("papp.products.EditProductView.StepDescription.new_variant_default_name", {
                  index: pvi + 1,
                }),
              }),
          })),
        }))
        .concat([
          {
            _focus: true,
            uuid: st.generateUuid(),
            active: true,
            position: Math.max(0, ...product.product_variants.map((pv) => pv.position)) + 1,
            translations: product.available_locales.map((locale) => ({
              uuid: st.generateUuid(),
              locale,
              name: I18n.t("papp.products.EditProductView.StepDescription.new_variant_default_name", {
                index: product.product_variants.length + 1,
                locale,
                defaultValue: I18n.t("papp.products.EditProductView.StepDescription.new_variant_default_name", {
                  index: product.product_variants.length + 1,
                }),
              }),
            })),
            coupon_codes: [],
            product_pricelist_items: [],
            payment_profile_product_attachments: [],
            product_pickup_points: [],
            pricelist_type: defaultPv.pricelist_type,
            stock_type: defaultPv.stock_type,
            allow_custom_pickup_point: defaultPv.allow_custom_pickup_point,
            allow_no_pickup_point: defaultPv.allow_no_pickup_point,
            custom_pickup_point_additional_fee: defaultPv.custom_pickup_point_additional_fee,
            default_requires_company_approval: defaultPv.default_requires_company_approval,
            default_total_stock_limit: defaultPv.default_total_stock_limit,
            nb_requires_company_approval: defaultPv.nb_requires_company_approval,
            nb_stock_useby_days_limit: defaultPv.nb_stock_useby_days_limit,
            nb_stock_useby_ends_at: defaultPv.nb_stock_useby_ends_at,
            nb_stock_useby_starts_at: defaultPv.nb_stock_useby_starts_at,
            nb_total_stock_limit: defaultPv.nb_total_stock_limit,
            nb_total_stock_used: 0,
            pickup_points_assignation_type: defaultPv.pickup_points_assignation_type,
            pickup_points_price_type: defaultPv.pickup_points_price_type,
          },
        ]),
    });
  }

  handleProductVariantChange(pv) {
    const { product, updateProduct, isTranslating } = this.props;
    if (isTranslating) return;
    updateProduct({
      ...product,
      product_variants: product.product_variants.map((x) => (x.uuid === pv.uuid ? { ...pv, _focus: undefined } : x)),
    });
  }

  handleProductVariantRemove(pv) {
    const { product, updateProduct, validate } = this.props;
    let nextProductVariants = product.product_variants.filter((x) => x.uuid !== pv.uuid);

    // Clean up the variant's name, if it's the only variant
    if (nextProductVariants.length === 1) {
      nextProductVariants = [
        {
          ...nextProductVariants[0],
          translations: nextProductVariants[0].translations.map((t) => ({
            ...t,
            name: null,
          })),
        },
      ];
    }

    updateProduct({
      ...product,
      product_variants: nextProductVariants,
    });
    validate();
  }

  handleProductVariantMigrateClick(pv) {
    const { product, updateProduct, validate } = this.props;
    openProductVariantMigrateModal({
      targetProduct: product,
      targetProductVariant: pv,
    }).then((nextProduct) => {
      if (nextProduct) {
        updateProduct(nextProduct);
        validate();
      }
    });
  }

  render() {
    const { currentCompany, readOnlyAccess, locale, product, submitLoading, valid, errors, validate } = this.props;
    const { activeIndex } = this.state;
    const translation = product.translations.find((t) => t.locale === locale) || {
      uuid: st.generateUuid(),
      locale,
    };
    if (!translation) {
      return null;
    }
    const translationName = `product.translations[${translation.uuid}]`;
    const pv = product.product_variants[0];
    const pvTranslation = product.product_variants[0].translations.find((t) => t.locale === locale) || {
      uuid: st.generateUuid(),
      locale,
    };
    const pvTranslationName = `product.product_variants[${pv.uuid}].translations[${pvTranslation.uuid}]`;
    const onBlur = validate;

    const showCustomContentOptions = [
      ["true", I18n.t("base.yes")],
      ["false", I18n.t("base.no")],
    ];

    return (
      <HintView.Container>
        <HintView.Content>
          <Form className="text" onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={submitLoading}>
            {!!errors.base && <FormMessage error={errors.base} />}

            <FormControl.InputFormGroup
              {...FormControl.addProps(`${translationName}.name`, { errors })}
              value={translation.name}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "name")}
              label={I18n.humanAttributeName("product", "name")}
              required
              readOnly={readOnlyAccess}
            />

            {st.authorize("update_slug", product) && (
              <FormControl.InputFormGroup
                {...FormControl.addProps(`${translationName}.slug`, { errors })}
                value={translation.slug}
                onBlur={onBlur}
                onChange={this.handleTranslationPropChange.bind(this, translation, "slug")}
                label={I18n.humanAttributeName("product", "slug")}
                readOnly={readOnlyAccess}
              />
            )}

            <FormControl.MultipleInputFormGroup
              {...FormControl.addProps(`${translationName}.highlights`, {
                errors,
              })}
              value={translation.highlights}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "highlights")}
              label={I18n.humanAttributeName("product", "highlights")}
              placeholder={I18n.humanAttributeName("product", "highlights_placeholder")}
              readOnly={readOnlyAccess}
            />

            {translation.deprecated_v1_short_description && (
              <FormControl.TextAreaFormGroup
                {...FormControl.addProps(`${translationName}.deprecated_v1_short_description`, { errors })}
                value={translation.deprecated_v1_short_description}
                onBlur={onBlur}
                onChange={this.handleTranslationPropChange.bind(this, translation, "deprecated_v1_short_description")}
                label={I18n.humanAttributeName("product", "deprecated_v1_short_description")}
                autoHeight
                readOnly={readOnlyAccess}
              />
            )}

            <FormControl.FormattableTextAreaFormGroup
              {...FormControl.addProps(`${translationName}.description`, {
                errors,
              })}
              value={translation.description}
              onBlur={onBlur}
              onChange={this.handleTranslationPropChange.bind(this, translation, "description")}
              label={I18n.humanAttributeName("product", "description")}
              readOnly={readOnlyAccess}
            />
            {product.product_type !== "coupon" && !readOnlyAccess && product.product_variants.length === 1 && (
              <div className="field">
                <Button
                  as="a"
                  basic
                  onClick={this.handleProductVariantMigrateClick.bind(this, product.product_variants[0])}
                  content={I18n.t("papp.products.EditProductView.StepDescription.migrate_data_from_other_product")}
                />
              </div>
            )}
            {!readOnlyAccess && product.product_variants.length === 1 && product.product_type !== "accommodation" && (
              <Message info>
                {I18n.tt("papp.products.EditProductView.StepDescription.add_variant_message", {
                  link: (
                    <Button
                      as="a"
                      className="link"
                      basic
                      color="blue"
                      onClick={this.handleProductVariantAdd.bind(this)}
                      content={I18n.t("papp.products.EditProductView.StepDescription.add_variant_message_link")}
                    />
                  ),
                })}
              </Message>
            )}
            {product.product_variants.length === 1 && (
              <div className="field">
                {product.product_type !== "coupon" && (
                  <FormControl.MultipleInputFormGroup
                    {...FormControl.addProps(`${pvTranslationName}.included_items`, { errors })}
                    value={pvTranslation.included_items}
                    onBlur={onBlur}
                    onChange={this.handlePvTranslationPropChange.bind(this, pv, pvTranslation, "included_items")}
                    label={I18n.humanAttributeName("product", "included_items")}
                    placeholder={I18n.humanAttributeName("product", "included_items_placeholder")}
                    readOnly={readOnlyAccess}
                  />
                )}
                {pvTranslation.deprecated_v1_includes_content !== null &&
                  pvTranslation.deprecated_v1_includes_content !== undefined && (
                    <FormControl.FormattableTextAreaFormGroup
                      {...FormControl.addProps(`${pvTranslationName}.deprecated_v1_includes_content`, { errors })}
                      value={pvTranslation.deprecated_v1_includes_content}
                      onBlur={onBlur}
                      onChange={this.handlePvTranslationPropChange.bind(
                        this,
                        pv,
                        pvTranslation,
                        "deprecated_v1_includes_content"
                      )}
                      label={I18n.humanAttributeName("product", "deprecated_v1_includes_content")}
                      autoHeight
                      readOnly={readOnlyAccess}
                    />
                  )}
                {product.product_type !== "coupon" && (
                  <FormControl.MultipleInputFormGroup
                    {...FormControl.addProps(`${pvTranslationName}.excluded_items`, { errors })}
                    value={pvTranslation.excluded_items}
                    onBlur={onBlur}
                    onChange={this.handlePvTranslationPropChange.bind(this, pv, pvTranslation, "excluded_items")}
                    label={I18n.humanAttributeName("product", "excluded_items")}
                    placeholder={I18n.humanAttributeName("product", "excluded_items_placeholder")}
                    readOnly={readOnlyAccess}
                  />
                )}
                {pvTranslation.deprecated_v1_excludes_content !== null &&
                  pvTranslation.deprecated_v1_excludes_content !== undefined && (
                    <FormControl.FormattableTextAreaFormGroup
                      {...FormControl.addProps(`${pvTranslationName}.deprecated_v1_excludes_content`, { errors })}
                      value={pvTranslation.deprecated_v1_excludes_content}
                      onBlur={onBlur}
                      onChange={this.handlePvTranslationPropChange.bind(
                        this,
                        pv,
                        pvTranslation,
                        "deprecated_v1_excludes_content"
                      )}
                      label={I18n.humanAttributeName("product", "deprecated_v1_excludes_content")}
                      autoHeight
                      readOnly={readOnlyAccess}
                    />
                  )}
                <FormControl.MultipleInputFormGroup
                  {...FormControl.addProps(`${pvTranslationName}.customer_requirements`, { errors })}
                  value={pvTranslation.customer_requirements}
                  onBlur={onBlur}
                  onChange={this.handlePvTranslationPropChange.bind(this, pv, pvTranslation, "customer_requirements")}
                  label={I18n.humanAttributeName("product", "customer_requirements")}
                  placeholder={I18n.humanAttributeName("product", "customer_requirements_placeholder")}
                  readOnly={readOnlyAccess}
                />
                {pvTranslation.deprecated_v1_requirements_content !== null &&
                  pvTranslation.deprecated_v1_requirements_content !== undefined && (
                    <FormControl.FormattableTextAreaFormGroup
                      {...FormControl.addProps(`${pvTranslationName}.deprecated_v1_requirements_content`, { errors })}
                      value={pvTranslation.deprecated_v1_requirements_content}
                      onBlur={onBlur}
                      onChange={this.handlePvTranslationPropChange.bind(
                        this,
                        pv,
                        pvTranslation,
                        "deprecated_v1_requirements_content"
                      )}
                      label={I18n.humanAttributeName("product", "deprecated_v1_requirements_content")}
                      autoHeight
                      readOnly={readOnlyAccess}
                    />
                  )}
              </div>
            )}

            {product.product_variants.length > 1 && (
              <div className="field">
                <Accordion styled fluid defaultActiveIndex={product.product_variants[1]._focus ? 1 : 0}>
                  {product.product_variants.map((pv) => {
                    const pvTranslation = pv.translations.find((t) => t.locale === locale) || {
                      uuid: st.generateUuid(),
                      locale,
                    };
                    const pvTranslationName = `product.product_variants[${pv.uuid}].translations[${pvTranslation.uuid}]`;
                    const error = !errors.hasValidProp(pvTranslationName);
                    return [
                      <Accordion.Title
                        key={`Title-{pv.uuid}`}
                        className={error ? "title--error" : undefined}
                        active={activeIndex === pv.uuid}
                        index={pv.uuid}
                        onClick={this.handleOpenAccordion}
                      >
                        {pv.name || I18n.t("papp.common.no_name")} <i className="icon dropdown" />
                      </Accordion.Title>,
                      <Accordion.Content active={activeIndex === pv.uuid}>
                        <ProductVariantFormSection
                          readOnlyAccess={readOnlyAccess}
                          locale={locale}
                          name={`product.product_variants[${pv.uuid}]`}
                          errors={errors}
                          product={product}
                          product_variant={pv}
                          onBlur={onBlur}
                          onChange={this.handleProductVariantChange.bind(this)}
                          onRemove={this.handleProductVariantRemove.bind(this, pv)}
                          onMigrateAttributesClick={this.handleProductVariantMigrateClick.bind(this, pv)}
                        />
                      </Accordion.Content>,
                    ];
                  })}
                  <Accordion.Title onClick={this.handleProductVariantAdd.bind(this)} className="new">
                    Dodaj nowy wariant <i className="icon material-icons">add</i>
                  </Accordion.Title>
                </Accordion>
              </div>
            )}

            {product.product_type === "standard" && (
              <FormControl.MultipleInputFormGroup
                {...FormControl.addProps(`${translationName}.customer_hints`, {
                  errors,
                })}
                value={translation.customer_hints}
                onBlur={onBlur}
                onChange={this.handleTranslationPropChange.bind(this, translation, "customer_hints")}
                label={I18n.humanAttributeName("product", "customer_hints")}
                placeholder={I18n.humanAttributeName("product", "customer_hints_placeholder")}
                readOnly={readOnlyAccess}
              />
            )}

            <Grid columns={2}>
              <Grid.Column>
                <FormControl.CategoriesInputFormGroup
                  {...FormControl.addProps(`product.category_ids`, { errors })}
                  value={product.category_ids}
                  onBlur={onBlur}
                  onChange={this.handlePropChange.bind(this, "category_ids")}
                  label={I18n.humanAttributeName("product", "categories")}
                  readOnly={readOnlyAccess}
                  type="ProductTag.category"
                />
              </Grid.Column>
              <Grid.Column>
                <FormControl.CategoriesInputFormGroup
                  {...FormControl.addProps(`product.location_ids`, { errors })}
                  value={product.location_ids}
                  onBlur={onBlur}
                  onChange={this.handlePropChange.bind(this, "location_ids")}
                  label={I18n.humanAttributeName("product", "locations")}
                  readOnly={readOnlyAccess}
                  type="ProductTag.location"
                />
              </Grid.Column>
            </Grid>

            <button type="submit" disabled={submitLoading} hidden />
          </Form>
        </HintView.Content>
        <HintView.Sidebar>
          <Hint />
        </HintView.Sidebar>
      </HintView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepDescription/StepDescriptionForm.jsx